import React, { useState, useEffect, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { formatDateTime, formatDate } from "../utils/dateUtils";
import { groupService } from "../services/groupService";

function capitalize(str) {
  if (!str) return "";
  // Replace dashes with spaces, then capitalize
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

export default function HistoryDetailsModal({ item, onClose, onUpdate, clientId, onOpenChat }) {
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterQuantity, setCounterQuantity] = useState(item.quantity || "");
  const [counterNotes, setCounterNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Load groups function
  const loadGroups = useCallback(async () => {
    if (!clientId) return;
    
    try {
      setLoadingGroups(true);
      const data = await groupService.getAllGroups(clientId);
      setGroups(data);
    } catch (err) {
      console.error('[HistoryDetailsModal] Failed to load groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, [clientId]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
    console.log("Item:"+JSON.stringify(item))
  }, [clientId, loadGroups, item]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setError('Please enter a group name');
      return;
    }

    if (!clientId) {
      setError('User not authenticated');
      return;
    }

    try {
      setCreatingGroup(true);
      setError(null);
      const newGroup = await groupService.createGroup(clientId, newGroupName.trim());
      await loadGroups();
      setNewGroupName("");
      setShowCreateGroup(false);
      // Optionally select the newly created group
      if (newGroup && newGroup.id) {
        setSelectedGroupId(newGroup.id);
      }
    } catch (err) {
      console.error('[HistoryDetailsModal] Failed to create group:', err);
      setError(err.message || 'Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAccept = async () => {
    if (!item.rfqNumber) {
      setError("Cannot accept: No RFQ number");
      return;
    }

    if (!window.confirm(`Accept RFQ ${item.rfqNumber}?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${item.rfqNumber}/accept?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: item.rfqNumber,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to accept RFQ: ${response.status}`);
      }

      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("RFQ accepted:", data);

      if (onUpdate) {
        onUpdate({ ...item, state: "Accepted", status: "accepted" });
      }

      onClose();
    } catch (err) {
      console.error("Accept error:", err);
      setError(err.message || "Failed to accept RFQ");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!item.rfqNumber) {
      setError("Cannot cancel: No RFQ number");
      return;
    }

    if (!window.confirm(`Are you sure you want to cancel RFQ ${item.rfqNumber}?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${item.rfqNumber}/reject?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: item.rfqNumber,
          reason: "Cancelled by client",
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel RFQ: ${response.status}`);
      }

      // Some backends return an empty body on success. Guard against calling
      // response.json() on empty responses which throws "Unexpected end of JSON input".
      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        // Non-JSON or empty body: ignore parsing error and keep data=null
        data = null;
      }
      console.log("RFQ cancelled:", data);

      // Notify parent to update
      if (onUpdate) {
        onUpdate({ ...item, state: "Rejected", status: "rejected" });
      }

      onClose();
    } catch (err) {
      console.error("Cancel error:", err);
      setError(err.message || "Failed to cancel RFQ");
    } finally {
      setLoading(false);
    }
  };

  const handleCounter = async () => {
    if (!item.rfqNumber) {
      setError("Cannot counter: No RFQ number");
      return;
    }

    console.log('[HistoryDetailsModal] Counter values before parsing:', { counterPrice, counterQuantity, counterNotes });
    
    const price = parseFloat(counterPrice);
    const quantity = parseFloat(counterQuantity);

    console.log('[HistoryDetailsModal] Parsed counter values:', { price, quantity, isNaNPrice: isNaN(price), isNaNQuantity: isNaN(quantity) });

    if (isNaN(price) || price <= 0) {
      setError("Please enter a valid price");
      return;
    }

    if (isNaN(quantity) || quantity <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      rfqId: item.rfqNumber,
      price,
      quantity,
      notes: counterNotes.trim(),
      timestamp: new Date().toISOString(),
    };
    
    console.log('[HistoryDetailsModal] Sending counter offer payload:', payload);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${item.rfqNumber}/counter?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit counter: ${response.status}`);
      }

      // Guard parsing like above; some endpoints may return minimal/empty bodies
      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("Counter submitted:", data);

      // Notify parent to update
      if (onUpdate) {
        onUpdate({ ...item, state: "Countered", status: "countered" });
      }

      onClose();
    } catch (err) {
      console.error("Counter error:", err);
      setError(err.message || "Failed to submit counter offer");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroupId) {
      setError("Please select a group");
      return;
    }

    if (!item.rfqNumber) {
      setError("Cannot add to group: No RFQ number");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await groupService.addRFQToGroup(clientId, selectedGroupId, item.rfqNumber);
      setSelectedGroupId("");
      alert("RFQ added to group successfully");
    } catch (err) {
      console.error('[HistoryDetailsModal] Failed to add RFQ to group:', err);
      setError(err.message || "Failed to add RFQ to group");
    } finally {
      setLoading(false);
    }
  };

  const isEditingField = () => {
    try {
      const ae = document.activeElement;
      if (!ae) return false;
      const tag = (ae.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return true;
      if (ae.isContentEditable) return true;
      return false;
    } catch (err) {
      return false;
    }
  };

  const handleBackdropClick = (e) => {
    // If the user is actively editing or selecting text in an input/textarea inside
    // the modal, do not treat the backdrop click as an intent to close. This prevents
    // accidental close when a drag/selection ends outside the modal.
    try {
      const selection = window.getSelection ? window.getSelection().toString() : '';
      if (selection && selection.length > 0) {
        // If there's a selection, and the active element is an input/textarea, don't close.
        if (isEditingField()) return;
      }
      // Also, if the active element is an input/textarea (user probably clicked outside while selecting), don't close
      if (isEditingField()) return;
    } catch (err) {
      // ignore errors and fall through to close
    }

    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">RFQ Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6 text-gray-900 dark:text-gray-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Event Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{item.event || "Event"}</h3>
              <span
                className={`px-3 py-1 rounded text-sm font-semibold ${
                  item.state === "Accepted"
                    ? "bg-green-100 text-green-800"
                    : item.state === "Rejected"
                    ? "bg-red-100 text-red-800"
                    : item.state === "Countered"
                    ? "bg-yellow-100 text-yellow-800"
                    : item.state === "Quoted"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {item.state}
              </span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {formatDateTime(item.time)}
            </div>
          </div>

          {/* RFQ Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Request Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              {item.rfqNumber && (
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">RFQ Number:</span>
                  <p className="text-gray-900 dark:text-gray-100">{item.rfqNumber}</p>
                </div>
              )}
              
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Dealer:</span>
                <p className="text-gray-900 dark:text-gray-100">{item.dealer}</p>
              </div>

             {item.dealerName && (
               <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Dealer Name:</span>
                  <p className="text-gray-900 dark:text-gray-100">{item.dealerName}</p>
              </div>
            )}
              
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Category:</span>
                <p className="text-gray-900 dark:text-gray-100">{capitalize(item.subMaterial)}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Material:</span>
                <p className="text-gray-900 dark:text-gray-100">{capitalize(item.material)}{item.subMaterial ? ` (${capitalize(item.subMaterial)})` : ''}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quantity:</span>
                <p className="text-gray-900 dark:text-gray-100">{item.quantity}</p>
              </div>
              
              {item.materialCategory && (
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Material Category:</span>
                  <p className="text-gray-900 dark:text-gray-100">{capitalize(item.materialCategory)}</p>
                </div>
              )}
            </div>
            
            {item.notes && (
              <div className="mt-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes:</span>
                <p className="text-gray-900 dark:text-gray-100 mt-1 whitespace-pre-wrap">{item.notes}</p>
              </div>
            )}
          </div>

          {/* Quote Information */}
          {item.quote && (
            <div className="space-y-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Quote Details
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                {item.quote.price !== null && item.quote.price !== undefined && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Price:</span>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      ${typeof item.quote.price === 'number' 
                        ? item.quote.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : item.quote.price}
                    </p>
                  </div>
                )}
                
                {item.quote.deliveryTime && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Delivery Time:</span>
                    <p className="text-gray-900 dark:text-gray-100">{item.quote.deliveryTime}</p>
                  </div>
                )}
                
                {item.quote.validUntil && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Valid Until:</span>
                    <p className="text-gray-900 dark:text-gray-100">{formatDate(item.quote.validUntil)}</p>
                  </div>
                )}
                
                {item.quote.createdAt && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quoted At:</span>
                    <p className="text-gray-900 dark:text-gray-100">{formatDateTime(item.quote.createdAt)}</p>
                  </div>
                )}
              </div>
              
              {item.quote.terms && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Terms:</span>
                  <p className="text-gray-900 mt-1">{item.quote.terms}</p>
                </div>
              )}
              
              {item.quote.notes && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Notes:</span>
                  <p className="text-gray-900 mt-1">{item.quote.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Add to Group Section */}
          {item.rfqNumber && (
            <div className="space-y-3 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Add to Group
              </h4>
              
              <div className="flex gap-2">
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={loadingGroups || loading}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Select a group...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.rfqs?.length || 0} RFQs)
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddToGroup}
                  disabled={!selectedGroupId || loading || loadingGroups}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              
              {!showCreateGroup && (
                <button
                  onClick={() => setShowCreateGroup(true)}
                  disabled={loading || loadingGroups}
                  className="w-full px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Group
                </button>
              )}
              
              {showCreateGroup && (
                <div className="space-y-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Group Name
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !creatingGroup) {
                        handleCreateGroup();
                      }
                    }}
                    placeholder="Enter group name..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingGroup}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateGroup}
                      disabled={creatingGroup || !newGroupName.trim()}
                      className="flex-1 px-3 py-2 bg-green-600 dark:bg-green-700 text-white font-medium rounded hover:bg-green-700 dark:hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {creatingGroup ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateGroup(false);
                        setNewGroupName("");
                        setError(null);
                      }}
                      disabled={creatingGroup}
                      className="px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Counter Offer Form */}
          {!showCounterForm && item.state !== "Rejected" && item.state !== "Accepted" && item.state !== "Awarded" && (
            <button
              onClick={() => {
                // Pre-fill counter price with quote price if available
                if (item.quote && item.quote.price) {
                  setCounterPrice(String(item.quote.price));
                }
                setShowCounterForm(true);
              }}
              disabled={loading}
              className="w-full px-4 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Submit Counter Offer
            </button>
          )}

          {/* Accept Button for Quoted State */}
          {item.state === "Quoted" && (
            <button
              onClick={handleAccept}
              disabled={loading}
              className="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "Accepting..." : "Accept Quote"}
            </button>
          )}

          {showCounterForm && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-gray-900">Counter Offer</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price ($)
                </label>
                <input
                  type="number"
                  value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)}
                  placeholder="Enter your price..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={counterQuantity}
                  onChange={(e) => setCounterQuantity(e.target.value)}
                  placeholder="Enter quantity..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={counterNotes}
                  onChange={(e) => setCounterNotes(e.target.value)}
                  placeholder="Add any additional notes..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleCounter}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white font-medium rounded hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : "Submit Counter"}
                </button>
                <button
                  onClick={() => {
                    setShowCounterForm(false);
                    setCounterPrice("");
                    setCounterQuantity(item.quantity || "");
                    setCounterNotes("");
                    setError(null);
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 font-medium rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3">
          {item.state !== "Rejected" && item.state !== "Accepted" && item.state !== "Awarded" && (
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-6 py-2 bg-red-600 dark:bg-red-700 text-white font-medium rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "Cancelling..." : "Cancel RFQ"}
            </button>
          )}
          {/* Open Chat Button */}
          <button
            onClick={() => {
              if (onOpenChat) {
                // Provide normalized RFQ info
                const rfq = {
                  rfqNumber: item.rfqNumber,
                  rfqId: item.rfqId || null,
                  dealer: item.dealer,
                  dealerName: item.dealer,
                  dealerId: item.dealerId || null,
                  material: item.material,
                };
                onOpenChat(rfq);
              }
            }}
            className="px-6 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
          >
            Open Chat
          </button>
          <button
            onClick={onClose}
            className="ml-auto px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
